import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { IndicatorDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { IndicatorRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIndicatorsQuery,
  GetIndicatorsQueryVariables,
  Indicator_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIndicatorsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { INDICATOR_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  normaliseArrayFilter,
  type ResultHistoryWindowArg,
  tagsWhereOnEntity,
  toResultHistoryVariables,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

/**
 * Maps TRPC indicator data to match the GraphQL query structure
 * Exported for use by data sources
 */
export function mapTrpcIndicatorsToGraphQL(
  trpcData: IndicatorRegisterResponse | undefined
): StrictMappedGraphQLSelection<GetIndicatorsQuery> | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    indicator: trpcData.indicators,
  };
}

type InternalArgs = UseGetIndicatorRegisterArgs & {
  entityIds?: string[];
};

const useIndicatorGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Indicator_Bool_Exp>(
    Parent_Type_Enum.Indicator
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: INDICATOR_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.indicatorFilterConditions,
  });

  // COI-837 — indicator is the one hook that REPLACES its nested where
  // wholesale: `orderedResults: results(where: $resultsWhere, …)` passes this
  // variable as the entire `where`, not as one member of an `_and`. That makes
  // `toResultHistoryVariables`' omit-when-unbounded contract load-bearing here
  // rather than merely tidy — `$resultsWhere` is non-null but defaulted `= {}`,
  // so sending `{}` explicitly would break the byte-identical contract for the
  // register page.
  const resultHistoryVariables =
    toResultHistoryVariables<GetIndicatorsQueryVariables>(
      args.resultHistoryWindow,
      (bounds) => ({ resultsWhere: { ResultDate: bounds } })
    );

  return { where, ...resultHistoryVariables };
};

const useGetIndicatorRegisterBase = createQueryHook<
  InternalArgs,
  IndicatorRegisterResponse,
  GetIndicatorsQuery
>({
  trpcQueryOptions: (
    trpc,
    {
      entityIds,
      tagTypeIds,
      departmentTypeIds,
      dateRange,
      ownership,
      resultHistoryWindow,
    }
  ) =>
    trpc.frontend.indicator.register.queryOptions({
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
      resultHistoryWindow,
    }),
  mapTrpcDataToGraphQL: (data) => ({ indicator: data.indicators }),
  graphqlDocument: GetIndicatorsDocument,
  graphqlVariables: useIndicatorGraphqlVariables,
});

export type UseGetIndicatorRegisterArgs =
  DashboardFilterArgs<IndicatorDashboardDateField> & ResultHistoryWindowArg;

/**
 * Fetches indicators with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses the `useEntityFilter` context internally —
 * consumers don't need to pass entityIds. Dashboard filters (COI-691) are all
 * optional.
 */
export const useGetIndicatorRegister = (params: {
  queryArgs: UseGetIndicatorRegisterArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetIndicatorRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
